<template>
	<div class="flex flex-col gap-5">
		<section class="gap-4 mb-3">
			<div>
				<div class="-mx-5 flex flex-col gap-3 p-5">
					<div class="relative z-10 flex flex-row flex-wrap w-full gap-5 items-center">
						<div class="relative">
							<ui-button @click.prevent="displayPeriods = !displayPeriods">
								<template #icon>
									<i class="fas fa-calendar-alt"></i>
								</template>
								<template #text>
									<div class="flex items-center gap-2">
										<span class="lg:block hidden">Période : </span><span>{{ selectedRangeLabel }}</span>
										<i class="fas fa-chevron-down"></i>
									</div>
								</template>
							</ui-button>
							<div
								v-if="displayPeriods"
								class="absolute z-50 w-full right-0 mt-2 grid grid-cols-1 divide-y bg-slate-50 rounded-md hover:shadow-md text-slate-500 border border-slate-800 overflow-x-hidden">
								<div
									v-for="range in statRangeList"
									:key="range.value"
									class="p-2 text-center cursor-pointer"
									:class="{
										'bg-slate-800 text-white': form.dateRange === range.value,
										'hover:text-black': form.dateRange !== range.value,
									}"
									@click.prevent="selectRange(range)">
									{{ range.label }}
								</div>
								<div v-if="form.dateRange === 'custom'" class="p-3 space-y-2 text-left">
									<label class="block text-sm">
										Du <input v-model="form.startDate" type="date" class="mt-1 w-full rounded border-slate-300" />
									</label>
									<label class="block text-sm">
										Au <input v-model="form.endDate" type="date" class="mt-1 w-full rounded border-slate-300" />
									</label>
									<p v-if="customRangeError" class="text-sm text-red-600">{{ customRangeError }}</p>
									<ui-button class="w-full" @click.prevent="applyCustomRange">
										<template #text>Appliquer</template>
									</ui-button>
								</div>
							</div>
						</div>
						<div class="relative">
							<ui-button @click.prevent="displayStatType = !displayStatType">
								<template #icon>
									<i class="fas fa-chart-bar"></i>
								</template>
								<template #text>
									<div class="flex items-center gap-2">
										<span>{{ statTypeList.find((item) => item.value === form.statType)?.label }}</span>
										<i class="fas fa-chevron-down"></i>
									</div>
								</template>
							</ui-button>
							<div
								v-if="displayStatType"
								class="absolute z-50 w-full right-0 mt-2 grid grid-cols-1 divide-y bg-slate-50 rounded-md hover:shadow-md text-slate-500 border border-slate-800 overflow-x-hidden">
								<div
									v-for="type in statTypeList"
									:key="type.value"
									class="p-2 text-center cursor-pointer"
									:class="{
										'bg-slate-800 text-white': form.statType === type.value,
										'hover:text-black': form.statType !== type.value,
									}"
									@click.prevent="selectStatType(type.value)">
									{{ type.label }}
								</div>
							</div>
						</div>
					</div>
					<div class="relative z-0 min-w-0">
						<div class="relative min-h-[350px]">
							<div
								class="bg-blue-100 text-blue-800 rounded-md absolute top-0 left-0 w-full flex items-center justify-center z-50 transition-height duration-300 ease"
								:class="{ 'h-full opacity-100': loading, 'h-0 opacity-0': !loading }">
								<i class="fa-duotone fa-solid fa-spinner-third fa-spin text-2xl"></i>
							</div>
							<!-- <ui-alert v-if="loading" type="info" class="mb-5">Chargement en cours</ui-alert> -->
							<apexchart v-if="!isEmpty" ref="chart" height="350" :options="options" :series="series"></apexchart>
							<ui-alert v-if="!loading && isEmpty" type="info" class="mb-5">Aucune donnée à afficher pour cette période</ui-alert>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script setup>
	import { computed, ref, watch, onMounted } from 'vue';
	import { usePage, Link } from '@inertiajs/vue3';
	import axios from 'axios';
	import _ from 'lodash';

	const page = usePage();

	const props = defineProps({
		arrayProId: {
			type: Array,
			default: () => ['all'],
		},
		prescribers: {
			type: Boolean,
			default: false,
		},
		lastVisite: {
			type: String,
			default: 'indisponible',
		},
	});

	const emit = defineEmits([]);

	const displayPeriods = ref(false);
	const displayStatType = ref(false);
	const toDateInput = (date) => {
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${date.getFullYear()}-${month}-${day}`;
	};
	const dateDaysAgo = (days) => {
		const date = new Date();
		date.setDate(date.getDate() - days);
		return toDateInput(date);
	};
	const dateMonthsAgo = (months) => {
		const date = new Date();
		date.setMonth(date.getMonth() - months);
		return toDateInput(date);
	};
	const form = ref({
		dateRange: '2months',
		startDate: dateMonthsAgo(2),
		endDate: toDateInput(new Date()),
		statType: 'allContracts',
	});
	const period = ref({});
	const previousPeriod = ref({});
	const statRangeList = ref([
		{ value: 'week', label: '7 derniers jours', days: 7 },
		{ value: '1months', label: '1 Mois', months: 1 },
		{ value: '2months', label: '2 Mois', months: 2 },
		{ value: '3months', label: '3 Mois', months: 3 },
		{ value: '4months', label: '4 Mois', months: 4 },
		{ value: '6months', label: '6 Mois', months: 6 },
		{ value: '12months', label: '12 Mois', months: 12 },
		{ value: 'custom', label: 'Période personnalisée' },
	]);
	const customRangeError = ref('');
	const formatPeriod = (range) => {
		if (!range?.start_date || !range?.end_date) return '';
		const format = (date) =>
			new Intl.DateTimeFormat('fr-FR', {
				day: 'numeric',
				month: 'long',
				year: 'numeric',
			}).format(new Date(`${date}T00:00:00`));
		return `${format(range.start_date)} – ${format(range.end_date)}`;
	};
	const selectedRangeLabel = computed(() => {
		if (form.value.dateRange === 'custom') return formatPeriod({ start_date: form.value.startDate, end_date: form.value.endDate });
		return statRangeList.value.find((item) => item.value === form.value.dateRange)?.label ?? '';
	});
	const statTypeList = ref([
		{ value: 'allContracts', label: 'Tous les contrats' },
		{ value: 'newInstallations', label: 'Nouvelles Installations' },
		{ value: 'newDesinstallations', label: 'Nouvelles désinstallations' },
	]);
	const isEmpty = ref(true);
	const loading = ref(false);
	const contractLoading = ref(false);
	const visitLoading = ref(true);
	const prescriberLoading = ref(true);
	const series = ref([]);
	const newContractsInfo = ref({
		data: [],
		bottomMessage: '',
	});
	const visitsInfo = ref({
		evolution: 0,
		bottomMessage: '',
	});
	const newPrescribersInfo = ref({
		evolution: 0,
		bottomMessage: '',
	});
	const options = ref({
		colors: ['#e1753a', '#285f81'],
		chart: {
			type: 'bar',
			parentHeightOffset: 0,
			stacked: false,
			toolbar: { show: false },
			zoom: { enabled: false },
		},
		plotOptions: { bar: { columnWidth: '50%', dataLabels: { position: 'top' } } },
		dataLabels: {
			enabled: true,
			offsetY: -20,
			style: { colors: ['#e1753a', '#285f81'] },
			background: { enabled: true, foreColor: '#fff', borderWidth: 0, opacity: 1 },
		},
		legend: {
			show: true,
		},
		fill: {
			type: 'solid',
		},
		stroke: {
			show: true,
			width: 1,
			colors: ['transparent'],
		},
		tooltip: {
			shared: true,
			intersect: false,
		},
		xaxis: {
			categories: [],
		},
	});

	const contractDialog = ref(null);

	const chart = ref(null);

	// Sorting state
	const sortColumn = ref(null);
	const sortDirection = ref(null);
	const originalContractData = ref(null);

	const selectRange = (range) => {
		displayPeriods.value = false;
		form.value.dateRange = range.value;
		customRangeError.value = '';
		if (range.value === 'custom') {
			displayPeriods.value = true;
			return;
		}

		form.value.endDate = toDateInput(new Date());
		form.value.startDate = range.days ? dateDaysAgo(range.days - 1) : dateMonthsAgo(range.months);
		loadData();
	};

	const applyCustomRange = () => {
		const duration = (Date.parse(form.value.endDate) - Date.parse(form.value.startDate)) / 86400000 + 1;
		if (!form.value.startDate || !form.value.endDate || Number.isNaN(duration) || duration < 7) {
			customRangeError.value = 'La période personnalisée doit couvrir au moins 7 jours.';
			return;
		}

		customRangeError.value = '';
		displayPeriods.value = false;
		loadData();
	};

	const selectStatType = (type) => {
		displayStatType.value = false;
		form.value.statType = type;
		loadData();
	};

	const loadData = () => {
		loading.value = true;
		axios
			.post(route('stats.index'), {
				pro_id: props.arrayProId,
				start_date: form.value.startDate,
				end_date: form.value.endDate,
				is_custom_range: form.value.dateRange === 'custom',
				type: form.value.statType,
			})
			.then((response) => {
				period.value = response.data.period || {};
				previousPeriod.value = response.data.previous_period || {};
				updateChart();
			})
			.catch((error) => console.log('error', error))
			.finally(() => (loading.value = false));
	};

	const updateChart = () => {
		const currentStatistics = period.value.statistics || {};
		const previousStatistics = previousPeriod.value.statistics || {};
		const categories = _.sortBy(_.union(_.keys(currentStatistics), _.keys(previousStatistics)));
		const seriesData = [
			{ name: formatPeriod(period.value), data: categories.map((activity) => currentStatistics[activity] || 0) },
			{ name: formatPeriod(previousPeriod.value), data: categories.map((activity) => previousStatistics[activity] || 0) },
		];
		options.value.xaxis.categories = categories;
		series.value = seriesData;

		// Mise à jour du graphique principal
		if (chart.value) {
			chart.value.updateOptions({
				xaxis: {
					categories: categories,
				},
			});
		}

		// Vérification si des données existent
		isEmpty.value = !seriesData.some((element) => element.data.some((value) => value > 0));
	};
	watch(
		() => props.arrayProId,
		(newVal) => {
			if (newVal.length > 0) {
				loadData();
			}
		},
		{ deep: true },
	);

	onMounted(() => {
		if (props.arrayProId.length > 0) {
			loadData();
		}
	});
</script>
